'use client';

import type { ProductRankingHistoryPoint } from 'src/types/product-rankings/product-rankings-domain';

import { Chart, useChart } from 'src/components/chart';

import { toRankValue } from '../utils/market-rankings-rank';
import { ymdToShort, compactWon, formatPrice } from '../utils/market-rankings-format';

/**
 * 순위·가격 이력 차트.
 *
 * ## ⚠️ 빠진 값은 `null`이어야 한다 — `NaN`이면 차트가 죽는다
 *
 * ApexCharts의 SVG path 파서가 `NaN` 좌표에서 "parser Error"를 던진다. 수집이 없는 날은
 * 숫자 대신 `null`을 넣어 **선을 끊는다.**
 *
 * ⚠️ 「빠진 값」을 가려내는 것은 `toRankValue` 다 — `Number(null)` 이 `0` 이라 직접
 * `Number()` 로 재면 **없는 날이 「0위」로 찍힌다.**
 *
 * ## ⚠️ 순위 축은 뒤집는다
 *
 * 1위가 위로 가야 한다(`reversed`). 가격 축은 점이 둘 이상 있을 때만 세운다 — 한 점짜리
 * 축은 눈금이 의미가 없다.
 */
/**
 * ⚠️ `currency` 는 **한 상품의 것**이다 — 이 차트는 상품 하나의 추이만 그리므로 통화가 하나다.
 * 안 넘기면 원화로 적는다(국내몰 넷의 기존 동작).
 */
export function RankPriceChart({
  points,
  height = 260,
  currency = 'KRW',
}: {
  points: ProductRankingHistoryPoint[];
  height?: number;
  currency?: string;
}) {
  // 축 라벨의 「만·천」 접기는 원화 감각이라 달러에는 안 쓴다
  const isWon = String(currency).toUpperCase() === 'KRW' || String(currency).toUpperCase() === 'KR';
  const categories = points.map((point) => ymdToShort(point.date));
  const rankData = points.map((point) => toRankValue(point.rank));
  const priceData = points.map((point) =>
    Number.isFinite(Number(point.displayPrice)) && Number(point.displayPrice) > 0
      ? Number(point.displayPrice)
      : null,
  );
  const hasPrice = priceData.filter((value) => value != null).length >= 2;
  const ranks = rankData.filter((rank): rank is number => rank != null);
  const maxRank = ranks.length ? Math.max(...ranks) : 10;

  const options = useChart({
    colors: ['#2E7D32', '#1877F2'],
    chart: { height, toolbar: { show: false }, parentHeightOffset: 0 },
    stroke: { width: hasPrice ? [3, 2] : [3], curve: 'straight' },
    markers: { size: 5, hover: { size: 7 } },
    xaxis: { categories, tooltip: { enabled: false } },
    yaxis: [
      {
        seriesName: '순위',
        reversed: true,
        min: 1,
        max: Math.max(maxRank, 3),
        forceNiceScale: true,
        tickAmount: Math.min(5, Math.max(maxRank, 3)),
        labels: { formatter: (value: number) => `${Math.round(value)}위` },
        title: { text: '순위', style: { fontWeight: 600 } },
      },
      {
        seriesName: '가격',
        opposite: true,
        show: hasPrice,
        labels: {
          formatter: (value: number) => (isWon ? compactWon(value) : formatPrice(value, currency)),
        },
        title: { text: '가격', style: { fontWeight: 600 } },
      },
    ],
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (value: number | null, opts?: { seriesIndex?: number }) => {
          if (value == null) return '-';
          if (opts?.seriesIndex === 0) return `${Math.round(value)}위`;
          return formatPrice(value, currency) || '-';
        },
      },
    },
    legend: { show: true, position: 'top', horizontalAlign: 'left', fontSize: '12px', fontWeight: 600 },
    grid: { strokeDashArray: 3, padding: { top: 0, right: 8, bottom: 0, left: 4 } },
  });

  const series: { name: string; type: string; data: (number | null)[] }[] = [
    { name: '순위', type: 'line', data: rankData },
  ];
  if (hasPrice) series.push({ name: '가격', type: 'line', data: priceData });

  return <Chart type="line" series={series} options={options} height={height} />;
}

/**
 * 타 채널 카드에 얹는 순위 스파크라인 — 값이 작을수록 위.
 *
 * ⚠️ 읽는 것이 `rank` 하나뿐이라 **점의 나머지 모양을 묻지 않는다.** 이력 점과 타채널
 * 점은 필드가 다른데 이 그림에는 그 차이가 상관없다.
 */
export function RankSparkline({
  points,
  color,
}: {
  points: { rank: number | null }[];
  color: string;
}) {
  const data = points.map((point) => toRankValue(point.rank));
  const options = useChart({
    colors: [color],
    chart: { sparkline: { enabled: true }, toolbar: { show: false } },
    stroke: { width: 2, curve: 'straight' },
    markers: { size: 0 },
    yaxis: { reversed: true },
    tooltip: { enabled: false },
    legend: { show: false },
  });
  return <Chart type="line" series={[{ name: '순위', data }]} options={options} height={34} />;
}
